const DEBUG_FRAME_PAD_PX = 4;

export type Bounds = { minX: number; maxX: number; minY: number; maxY: number };

export function mergeBounds(a: Bounds | null, b: Bounds): Bounds {
  if (a === null) {
    return b;
  }
  return {
    minX: Math.min(a.minX, b.minX),
    maxX: Math.max(a.maxX, b.maxX),
    minY: Math.min(a.minY, b.minY),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

function rootLayoutScale(root: HTMLElement): number {
  if (root.offsetWidth <= 0) {
    return 1;
  }
  return root.getBoundingClientRect().width / root.offsetWidth;
}

function partVisible(el: HTMLElement): boolean {
  return el.style.visibility !== "hidden" && Number(el.style.opacity) > 0.01;
}

export function elementBoundsInRoot(
  root: HTMLElement,
  el: HTMLElement,
): Bounds {
  const origin = root.getBoundingClientRect();
  const rect = el.getBoundingClientRect();
  const inv = 1 / rootLayoutScale(root);
  return {
    minX: (rect.left - origin.left) * inv,
    minY: (rect.top - origin.top) * inv,
    maxX: (rect.right - origin.left) * inv,
    maxY: (rect.bottom - origin.top) * inv,
  };
}

function padBounds(box: Bounds, pad: number): Bounds {
  return {
    minX: box.minX - pad,
    minY: box.minY - pad,
    maxX: box.maxX + pad,
    maxY: box.maxY + pad,
  };
}

function markerPart(
  root: HTMLElement,
  characterId: string,
  part: "disc" | "name",
): HTMLElement | null {
  return root.querySelector<HTMLElement>(
    `[data-marker-part="${part}"][data-character-id="${characterId}"]`,
  );
}

export function characterPortraitBounds(
  root: HTMLElement,
  characterId: string,
): Bounds | null {
  const el = markerPart(root, characterId, "disc");
  if (el === null || !partVisible(el)) {
    return null;
  }
  return padBounds(elementBoundsInRoot(root, el), DEBUG_FRAME_PAD_PX);
}

export function characterNameBounds(
  root: HTMLElement,
  characterId: string,
): Bounds | null {
  const el = markerPart(root, characterId, "name");
  if (el === null || !partVisible(el)) {
    return null;
  }
  return padBounds(elementBoundsInRoot(root, el), DEBUG_FRAME_PAD_PX);
}

export function characterBounds(
  root: HTMLElement,
  characterId: string,
): Bounds | null {
  let box: Bounds | null = null;
  const portrait = characterPortraitBounds(root, characterId);
  const name = characterNameBounds(root, characterId);
  if (portrait !== null) {
    box = mergeBounds(box, portrait);
  }
  if (name !== null) {
    box = mergeBounds(box, name);
  }
  return box;
}

export function characterIdsBounds(
  root: HTMLElement,
  characterIds: readonly string[],
): Bounds | null {
  let box: Bounds | null = null;
  for (const characterId of characterIds) {
    const next = characterBounds(root, characterId);
    if (next === null) {
      continue;
    }
    box = mergeBounds(box, next);
  }
  return box;
}

export function applyDebugFrame(
  frame: HTMLElement,
  box: Bounds | null,
): void {
  if (box === null) {
    frame.style.display = "none";
    return;
  }
  frame.style.display = "block";
  frame.style.left = "0";
  frame.style.top = "0";
  frame.style.transform = `translate(${box.minX}px, ${box.minY}px)`;
  frame.style.width = `${box.maxX - box.minX}px`;
  frame.style.height = `${box.maxY - box.minY}px`;
}
